<template>
  <PropertyOnboardingStep
    :is-next-button-disabled="form.validation.$invalid"
    title="What are the listing basics?"
    :submit="submit"
  >
    <FormGroup>
      <FormField
        v-if="isApartmentNumberFieldRequired"
        label="Apartment number"
        :error="form.fieldErrors('apartmentNumber')"
      >
        <Input v-model="form.fields.apartmentNumber" name="apartmentNumber" placeholder="Type apartment number">
          <template #prefix>#</template>
        </Input>
      </FormField>

      <FormField label="Property Size" :error="form.fieldErrors('squareFeet')">
        <Input
          v-model="form.fields.squareFeet"
          type="number"
          min="0"
          name="squareFeet"
          placeholder="Enter a whole number, e.g. 850"
          @keydown="forbidCharacters"
        >
          <template #suffix>ft²</template>
        </Input>
      </FormField>

      <FormField v-if="isBedroomFieldVisible" label="Bedrooms" direction="row" :error="form.fieldErrors('bedrooms')">
        <InputStepper v-model="form.fields.bedrooms" :min="1" name="bedrooms" />
      </FormField>

      <FormField label="Full bathrooms" direction="row" :error="form.fieldErrors('fullBathrooms')">
        <InputStepper v-model="form.fields.fullBathrooms" :min="0" name="fullBathrooms" />
      </FormField>

      <FormField label="Half bathrooms" direction="row" :error="form.fieldErrors('halfBathrooms')">
        <InputStepper v-model="form.fields.halfBathrooms" :min="0" name="halfBathrooms" />
      </FormField>
    </FormGroup>
  </PropertyOnboardingStep>
</template>

<script setup lang="ts">
import { minValue, numeric, required, requiredIf } from '@vuelidate/validators';
import { watch, computed } from 'vue';

import { FormField, FormGroup, Input, InputStepper, useForm } from '@tallo/design-system';
import {
  SaveLocationDto,
  usePropertyStore,
  isApartmentNumberRequired,
  SavePropertySpecificationsDto,
  isBedroomFieldRequired,
  useUnitDetailsLookup,
  fillUntouchedFields,
} from '@tallo/investor/property';
import { PropertySpecificationsDto } from '@tallo/property';

import PropertyOnboardingStep from '../property-onboarding-step.vue';

const propertyType = computed(() => propertyStore.property?.specifications?.propertyType);
// A unit in a building keeps the number it was added with, since the building owns its address
const isApartmentNumberFieldRequired = computed(
  () => !propertyStore.property?.buildingId && isApartmentNumberRequired(propertyType.value),
);
const isBedroomFieldVisible = computed(() => isBedroomFieldRequired(propertyType.value));

const propertyStore = usePropertyStore();
const form = useForm<SavePropertySpecificationsDto & Pick<SaveLocationDto, 'apartmentNumber'>>(
  {
    apartmentNumber: null,
    squareFeet: null,
    bedrooms: 1,
    fullBathrooms: null,
    halfBathrooms: null,
  },
  {
    apartmentNumber: { required: requiredIf(() => isApartmentNumberFieldRequired.value) },
    squareFeet: { required, numeric, minValue: minValue(1) },
    bedrooms: { required: requiredIf(() => isBedroomFieldVisible.value), numeric, minValue: minValue(1) },
    fullBathrooms: { required, numeric, minValue: minValue(0) },
    halfBathrooms: { required, numeric, minValue: minValue(0) },
  },
);

async function submit() {
  return await form.submit((fields) => {
    const apartmentNumber = isApartmentNumberFieldRequired.value ? fields.apartmentNumber : null;

    const specificationsData = {
      ...fields,
      bedrooms: isBedroomFieldVisible.value ? fields.bedrooms : 0,
    };

    return Promise.all([
      propertyStore.saveSpecifications(specificationsData),
      ...(propertyStore.property?.buildingId ? [] : [propertyStore.saveLocation({ apartmentNumber })]),
    ]);
  });
}

function forbidCharacters(event: KeyboardEvent) {
  if (event.key === '.' || event.key === ',' || event.key.toLowerCase() === 'e') {
    event.preventDefault();
  }
}

type RoomFields = Pick<SavePropertySpecificationsDto, 'squareFeet' | 'bedrooms' | 'fullBathrooms' | 'halfBathrooms'>;

// What the form was last filled with, so a unit found later only replaces what the owner left alone
let filled: RoomFields = { squareFeet: null, bedrooms: null, fullBathrooms: null, halfBathrooms: null };

function populateSpecificationFields(specifications?: PropertySpecificationsDto) {
  filled = {
    squareFeet: specifications?.squareFeet ?? null,
    bedrooms: specifications?.bedrooms || 1,
    fullBathrooms: specifications?.fullBathrooms ?? 1,
    halfBathrooms: specifications?.halfBathrooms ?? 0,
  };
  Object.assign(form.fields, filled);
}

function populateLocationFields(apartmentNumber?: string | null) {
  Object.assign(form.fields, { apartmentNumber: apartmentNumber ?? null });
}

function populateFromUnit(unit: Partial<PropertySpecificationsDto> | null) {
  fillUntouchedFields<RoomFields>(form.fields, filled, {
    squareFeet: unit?.squareFeet,
    // The bedrooms field starts at 1; a studio has no bedrooms field to fill
    bedrooms: unit?.bedrooms && unit.bedrooms >= 1 ? unit.bedrooms : undefined,
    fullBathrooms: unit?.fullBathrooms,
    halfBathrooms: unit?.halfBathrooms,
  });
}

// Only a unit typed here is looked up: one already saved was filled when it was entered
const { specifications: unitSpecifications } = useUnitDetailsLookup(() => {
  const location = propertyStore.property?.location;
  const apartmentNumber = form.fields.apartmentNumber?.trim();

  if (!isApartmentNumberFieldRequired.value || !location || !apartmentNumber) {
    return null;
  }

  if (apartmentNumber === location.apartmentNumber?.trim()) {
    return null;
  }

  return { address: location.address, city: location.city, state: location.state, zip: location.zip, apartmentNumber };
});

watch(() => propertyStore.property?.specifications, populateSpecificationFields, { immediate: true });
watch(() => propertyStore.property?.location?.apartmentNumber, populateLocationFields, { immediate: true });
watch(unitSpecifications, populateFromUnit);
</script>
